import React, { useState } from 'react';
import MainLayout from 'components/layout/MainLayout';
import { ASSET_PREFIX } from 'utils/constants';
import HeroSection from 'components/HeroSection';
import { NextSeo } from 'next-seo';
import PaddedMaxWidthContainer from 'components/layout/PaddedMaxWidthContainer';
import HorizontalShard from 'components/HorizontalShard';
import OverviewSection from 'components/iceland-raffle/OverviewSection';
import WhatsIncludedSection from 'components/iceland-raffle/WhatsIncludedSection';
import PlantSeaTreeSection from 'components/iceland-raffle/PlantSeaTreeSection';
import TalkOfTownSection from 'components/iceland-raffle/TalkOfTheTownSection';
import { scrollToSection, sleep } from 'utils/common';
import Link from 'next/link';
import { getCurrentDraw, getIcelandRaffleTicketAvailability } from 'api/raffle';
import dayjs from 'dayjs';
import RoundedButton, { ColorStyle } from 'components/RoundedButton';
import NewRaffleTicket from 'components/iceland-raffle/NewRaffleTicket';

const IcelandRafflePage = ({ ticketCap, ticketCount, drawDateString }) => {
  const drawDate = dayjs(drawDateString);

  const showProgressBars = true;

  const [showFullTerms, setShowFullTerms] = useState(false);

  const showVideoHero = true; // Video hero test is temporarily paused; show all users the video in the meantime.

  const viewFullTerms = async () => {
    setShowFullTerms(true);
    await sleep(100); // Allow time for the component to update.
    scrollToSection('#fullTermsSection');
  };

  return (
    <MainLayout>
      <NextSeo
        title="Win a Trip To Iceland | Stunning Getaway for 2 | DAYMADE"
        description="Win a trip to Iceland in DAYMADE's online competition. This once-in-a-lifetime getaway for two could be yours - make sure you're in with a chance of winning."
        openGraph={{
          title: 'Win a Trip To Iceland | Stunning Getaway for 2 | DAYMADE',
          description:
            "Win a trip to Iceland in DAYMADE's online competition. This once-in-a-lifetime getaway for two could be yours - make sure you're in with a chance of winning.",
        }}
        // additionalMetaTags={[{
        //   name: 'keywords',
        //   content: 'daymade raffle,raffle,iceland,win a holiday,iceland trip,competition,holiday competition,seatrees,plant a tree,win a holiday iceland,travel competition,travel,ion adventure hotel,Þríhnúkagígur volcano,reykjavik,london to reykjavik',
        // }]}
      />

      <HeroSection
        dimBackgroundOpacity={0.4}
        heroUrl={`${ASSET_PREFIX}/images/iceland-raffle/hero.jpg`}
        videoProps={
          showVideoHero
            ? {
                portrait: {
                  vimeoId: '572021441',
                  width: '1080',
                  height: '1500',
                  poster: `${ASSET_PREFIX}/images/iceland-raffle/hero-portrait-poster.jpg`,
                },
                landscape: {
                  vimeoId: '572012561',
                  width: '1920',
                  height: '1080',
                  poster: `${ASSET_PREFIX}/images/iceland-raffle/hero-landscape-poster.jpg`,
                },
              }
            : null
        }
        upcomingDrawDate={drawDate}
        raffle
        height="auto"
        largerHeight="500px"
        wideDesktopHeight="600px"
        widestDesktopHeight="750px"
      >
        <PaddedMaxWidthContainer
          style={{
            position: 'relative',
            padding: '80px 2em 40px',
            alignItems: 'flex-start',
          }}
        >
          <img
            src={`${ASSET_PREFIX}/images/iceland-raffle/guaranteed-winner-badge.svg`}
            style={{
              width: '30%',
              maxWidth: '150px',
              position: 'absolute',
              right: 10,
              top: -10,
            }}
            alt="Guaranteed winner"
            onClick={() => scrollToSection('#summary')}
          />

          <div
            style={{
              textShadow: '1.5px 1.5px #4a4a4a',
              fontSize: '1.8em',
              fontWeight: '700',
              textAlign: 'left',
            }}
          >
            Win a trip for 2 to
            <div
              className="spacedFont"
              style={{ fontWeight: 900, fontSize: '1.5em' }}
            >
              Iceland
            </div>
            <em>worth £10,000!</em>
          </div>

          <div
            style={{
              textShadow: '1.5px 1.5px #4a4a4a',
              fontSize: '1.8em',
              fontWeight: '700',
              textTransform: 'uppercase',
            }}
          >
            <br />
            (Raffle Closed — Winner Announced 28 September)
          </div>
        </PaddedMaxWidthContainer>
      </HeroSection>

      <HorizontalShard
        background={`${ASSET_PREFIX}/images/iceland-raffle/orange-shard-1.svg`}
        topLeftInset={25}
        topRightInset={0}
        bottomRightInset={0}
        bottomLeftInset={25}
        style={{
          marginTop: '-65px',
          marginBottom: '-65px',
          backgroundPosition: 'center',
          padding: '2em',
        }}
      >
        <PaddedMaxWidthContainer
          style={{ padding: 0, margin: '0 auto', width: '100%' }}
        >
          <div
            className="trustpilotSeaTreesContainer"
            style={{
              display: 'flex',
              justifyContent: 'space-around',
              alignItems: 'center',
              padding: '2em 0',
              maxWidth: '450px',
              width: '100%',
              alignSelf: 'flex-end',
            }}
          >
            <a
              href="https://www.trustpilot.com/review/daymade.co.uk"
              target="_blank"
              rel="noreferrer"
              style={{
                width: '30%',
                maxWidth: '200px',
                alignSelf: 'flex-end',
                paddingTop: '1.2em',
              }}
            >
              <img
                src={`${ASSET_PREFIX}/images/iceland-raffle/trustpilot.svg`}
                style={{
                  width: '100%',
                }}
                alt="Rated 'Excellent' on Trustpilot"
              />
            </a>

            <img
              src={`${ASSET_PREFIX}/images/iceland-raffle/sea-trees.png`}
              alt="SeaTrees by Sustainable Surf logo"
              style={{
                width: '30%',
                maxWidth: '200px',
              }}
            />
          </div>
        </PaddedMaxWidthContainer>
      </HorizontalShard>

      <OverviewSection
        ctaHandler={() => scrollToSection('#chooseEntryCountSection')}
        ticketCap={ticketCap}
        ticketCount={ticketCount}
        showProgressBar={showProgressBars}
        drawDate={drawDate}
      />

      <WhatsIncludedSection
        ctaHandler={() => scrollToSection('#chooseEntryCountSection')}
        onViewFullTerms={viewFullTerms}
      />

      <HorizontalShard
        background={`${ASSET_PREFIX}/images/iceland-raffle/orange-shard-2.svg`}
        topLeftInset={0}
        topRightInset={25}
        bottomRightInset={25}
        bottomLeftInset={0}
        style={{
          marginTop: '-65px',
          marginBottom: '-65px',
          backgroundPosition: 'center',
          padding: '2em',
        }}
      >
        <PaddedMaxWidthContainer
          style={{ padding: 0, margin: '0 auto', width: '100%' }}
        >
          <div
            style={{
              display: 'flex',
              justifyContent: 'space-around',
              alignItems: 'center',
              padding: '2em 0',
              maxWidth: '650px',
              width: '100%',
              alignSelf: 'flex-start',
              textShadow: '1.5px 1.5px white',
              textAlign: 'center',
            }}
          >
            <div style={{ margin: '0.6em' }}>
              <img
                src={`${ASSET_PREFIX}/images/iceland-raffle/calendar-icon-2.svg`}
                alt=""
                style={{
                  height: '30px',
                  maxWidth: '200px',
                }}
              />
              <p>Flexible dates</p>
            </div>

            <div style={{ margin: '0.6em' }}>
              <img
                src={`${ASSET_PREFIX}/images/iceland-raffle/plane-icon.svg`}
                alt=""
                style={{
                  height: '30px',
                  maxWidth: '200px',
                }}
              />
              <p>Return Flights x 2</p>
            </div>

            <div style={{ margin: '0.6em' }}>
              <img
                src={`${ASSET_PREFIX}/images/iceland-raffle/hotel-icon.svg`}
                alt=""
                style={{
                  height: '30px',
                  maxWidth: '200px',
                }}
              />
              <p>Luxury Hotel Stay</p>
            </div>

            <div style={{ margin: '0.6em' }}>
              <img
                src={`${ASSET_PREFIX}/images/iceland-raffle/activities-icon.png`}
                alt=""
                style={{
                  height: '30px',
                  maxWidth: '200px',
                }}
              />
              <p>Unforgettable Activities</p>
            </div>
          </div>
        </PaddedMaxWidthContainer>
      </HorizontalShard>

      <PlantSeaTreeSection
        ctaHandler={() => scrollToSection('#chooseEntryCountSection')}
      />

      <PaddedMaxWidthContainer
        id="chooseEntryCountSection"
        wavyBackground
        style={{ maxWidth: '900px', padding: '4em 0 6em', textAlign: 'center' }}
      >
        <h3>
          Raffle <strong>Closed</strong>
        </h3>

        <p>
          Massive congrats to <strong>James</strong> for winning our Iceland
          Raffle!
        </p>

        <NewRaffleTicket
          lines={['ICE 4471']}
          style={{ width: '190px' }}
          rafflePrizeGroup="ICELAND"
        />

        <p>
          Too late to enter the Iceland Raffle? Fear not! We have an epic
          Iceland prize in our Weekly Draws too.
        </p>

        <RoundedButton
          href="/prizes/details/iceland-northern-lights"
          colorStyle={ColorStyle.Iceland}
        >
          Check It Out Here
        </RoundedButton>

        <p style={{ fontStyle: 'italic', margin: '1.6em' }}>
          The Organiser’s decision is final and no correspondence will be
          entered into about the result of the Competition following the
          determination of the Winner.
        </p>
      </PaddedMaxWidthContainer>

      <HorizontalShard
        background={`${ASSET_PREFIX}/images/iceland-raffle/orange-shard-1.svg`}
        topLeftInset={4}
        topRightInset={0}
        bottomRightInset={0}
        bottomLeftInset={0}
        style={{
          marginTop: '-65px',
          backgroundPosition: 'center',
          padding: '2em',
        }}
      >
        <TalkOfTownSection onViewFullTerms={viewFullTerms} />
      </HorizontalShard>

      {showFullTerms && (
        <PaddedMaxWidthContainer
          id="fullTermsSection"
          style={{ padding: '2em' }}
        >
          <h3>Terms</h3>

          <ul>
            <li>
              Max entries 2,401. Raffle closes when all entries have been sold.
            </li>
            <li>
              All elements of the prize package are subject to availability at
              the time of booking and exclude sold out dates where applicable.{' '}
            </li>
            <li>
              This prize can be redeemed between 1st of June and 20th of October
              2021 or 2022. Winner must provide 6 weeks notice before preferred
              departure date.
            </li>
            <li>
              The prize consists of a 5 night break to Iceland for 2 people
              including accommodation at The ION Adventure Hotel
            </li>
            <li>
              The prize includes return economy flights from the winners nearest
              mainland airport to Reykjavik, Iceland including return transfers
              from the destination airport to the hotel.{' '}
            </li>
            <li>
              You and your chosen guest will be responsible for travel
              insurance, travel to and from your departure airport and all other
              personal expenses not detailed in the prize breakdown.{' '}
            </li>
            <li>
              Accommodation will be at the TheION Adventure Hotel in a Deluxe
              Double, where available, on a room only basis.
            </li>
            <li>
              Private Jeep transfers will take the winners to the private dining
              experience inside the volcano on the date arrange for the
              experience.{' '}
            </li>
            <li>
              A private dinner inside the volcano will be arranged for the
              winners by a chef. Dietary requirements and special requests will
              be arranged in advance with the Fulfilment Manager.{' '}
            </li>
            <li>
              You both must have a valid machine readable passport with minimum
              6 month validity beyond the return date, with at least 2 unstamped
              pages and a visa (if applicable).{' '}
            </li>
            <li>
              Travel is available for one year after the winner has been
              notified (subject to availability) but excludes Christmas and New
              Year (18th December 2021 –8th January 2022) and Public and Bank
              Holiday periods and once booked cannot be amended.{' '}
            </li>
            <li>
              AnotherWayLtd exercises a COVID 19 policy that includes extended
              flexibility for bookings based on the status of the UK and the
              destinations specific Coronavirus status’ at the time of booking.
              This may include, but is not limited to; extending the period of
              redemption and/or substituting the experience to one that is
              materially different from the original.
            </li>
            <li>
              The prize winner must provide 6 weeks’ notice before preferred
              departure date.{' '}
            </li>
            <li>
              At least one of the travelling party must be 18 years of age or
              older. You must be over 18 years of age to enter the Iceland
              Raffle.{' '}
            </li>
            <li>
              Additionally, guests below the age of 16 must provide written
              parental consent to travel unless accompanied by a parent or
              guardian.{' '}
            </li>
            <li>Terms and Conditions of the carriers apply. </li>
            <li>
              AnotherWay Ltd retains the right to substitute any element of the
              package for a similar activity, hotel, or experience in the event
              of unavailability or cancellation. The winners will be consulted
              and managed throughout the process at all times.
            </li>
            <li>
              View full {''}
              <Link href="/terms-and-conditions">
                <a target="_blank">Terms & Conditions</a>
              </Link>
              .
            </li>
          </ul>
        </PaddedMaxWidthContainer>
      )}

      <style jsx>
        {`
          :global(.footer) {
            margin-top: 0 !important;
          }
        `}
      </style>
    </MainLayout>
  );
};

export const getServerSideProps = async () => {
  const { ticketCap, ticketCount } = await getIcelandRaffleTicketAvailability();
  const drawDateString = (await getCurrentDraw(`ICELAND`)).draw_date;
  return {
    props: {
      ticketCap: ticketCap ?? null,
      ticketCount: ticketCount ?? null,
      drawDateString: drawDateString ?? null,
    },
  };
};

export default IcelandRafflePage;
